import { pipeState } from '../utils';
import {
  BaseEntity,
  CorrelationId,
  DEFAULT_SCOPE_NAME,
  EntityAdapterOptions,
  EntityDataAdapter,
  EntityMetaAdapter,
  EntityState,
  ID,
  IdSelector,
  OptimisticUpdate,
} from './types';
import { resolveScope } from './utils';

export const createHydratedEntityState = <T>(
  initialState: EntityState<T>,
  entities: Record<string, T[]> | T[],
  adapter: EntityDataAdapter<T> & EntityMetaAdapter<T>,
): EntityState<T> => {
  if (Array.isArray(entities)) {
    return adapter.setMany(initialState, entities, { scope: DEFAULT_SCOPE_NAME });
  } else {
    const transforms = Object.entries(entities).map(
      ([scope, entities]) =>
        (state: EntityState<T>) =>
          adapter.setMany(state, entities, { scope }),
    );

    return pipeState(initialState, ...transforms);
  }
};

export const getEntityDataAdapter = <T extends BaseEntity>(
  selectId: IdSelector<T>,
  initialState: EntityState<T>,
) => {
  const adapter: EntityDataAdapter<T> = {
    addOne: (
      state: EntityState<T>,
      data: T,
      options: EntityAdapterOptions & CorrelationId = {},
    ): EntityState<T> => {
      const scopeName = resolveScope(options.scope);
      const scopeData = state.scope[scopeName] || [];
      const entityId = options.correlationId || selectId(data);

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [entityId]: data,
        },
        scope: {
          ...state.scope,
          [scopeName]: [...new Set([...scopeData, entityId])],
        },
      };
    },
    addMany: (
      state: EntityState<T>,
      data: T[],
      options: EntityAdapterOptions = {},
    ): EntityState<T> => {
      const scopeName = resolveScope(options.scope);
      const scopeData = state.scope[scopeName] || [];

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          ...Object.fromEntries(data.map(item => [selectId(item), item])),
        },
        scope: {
          ...state.scope,
          [scopeName]: [...new Set([...scopeData, ...data.map(selectId)])],
        },
      };
    },
    setOne: (
      state: EntityState<T>,
      data: T,
      options: EntityAdapterOptions = {},
    ): EntityState<T> => {
      const scopeName = resolveScope(options.scope);

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [selectId(data)]: data,
        },
        scope: {
          ...state.scope,
          [scopeName]: [selectId(data)],
        },
      };
    },
    setMany: (
      state: EntityState<T>,
      data: T[],
      options: EntityAdapterOptions = {},
    ): EntityState<T> => {
      const scopeName = resolveScope(options.scope);

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          ...Object.fromEntries(data.map(item => [selectId(item), item])),
        },
        scope: {
          ...state.scope,
          [scopeName]: data.map(selectId),
        },
      };
    },
    writeOne: (state: EntityState<T>, data: T): EntityState<T> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        [selectId(data)]: data,
      },
    }),
    writeMany: (state: EntityState<T>, data: T[]): EntityState<T> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        ...Object.fromEntries(data.map(item => [selectId(item), item])),
      },
    }),
    replaceOne: (state: EntityState<T>, previousId: ID, current: T): EntityState<T> => {
      const { [previousId]: _, ...entityMap } = state.entityMap;
      const { [previousId]: previousEntityMeta, ...entityMetaMap } = state.entityMetaMap;

      const currentId = selectId(current);

      return {
        ...state,
        entityMap: {
          ...entityMap,
          [currentId]: current,
        },
        entityMetaMap: {
          ...entityMetaMap,
          [currentId]: previousEntityMeta,
        },
        scope: Object.fromEntries(
          Object.entries(state.scope).map(([scopeName, ids]) => [
            scopeName,
            ids.map(id => (id === previousId ? currentId : id)),
          ]),
        ),
      };
    },
    replaceMany: (
      state: EntityState<T>,
      data: { previousId: ID; current: T }[],
    ): EntityState<T> => {
      const replacementMap = Object.fromEntries(data.map(item => [item.previousId, item]));
      const replace = <O extends Record<string | number, any>>(
        object: O,
        replacer: (key: keyof O, value: O[keyof O]) => [keyof O, O[keyof O]],
      ) => Object.fromEntries(Object.entries(object).map(([key, value]) => replacer(key, value)));

      return {
        ...state,
        entityMap: replace(state.entityMap, (id, entity) => {
          const replacement = replacementMap[id];
          return replacement ? [selectId(replacement.current), replacement.current] : [id, entity];
        }),
        entityMetaMap: replace(state.entityMetaMap, (id, meta) => {
          const replacement = replacementMap[id];
          return replacement ? [selectId(replacement.current), meta] : [id, meta];
        }),
        scope: replace(state.scope, (scopeName, ids) => [
          scopeName,
          ids.map(id => (replacementMap[id] ? selectId(replacementMap[id].current) : id)),
        ]),
      };
    },
    removeOne: (state: EntityState<T>, id: ID): EntityState<T> => adapter.removeMany(state, [id]),
    removeMany: (state: EntityState<T>, idsToRemove: ID[]): EntityState<T> => {
      const stringIds = idsToRemove.map(id => `${id}`);
      const scope = Object.entries(state.scope).reduce((acc, [scopeName, ids]) => {
        acc[scopeName] = ids.filter(id => !stringIds.includes(`${id}`));
        return acc;
      }, <typeof state.scope>{});

      return {
        ...state,
        scope,
        entityMap: Object.fromEntries(
          Object.entries(state.entityMap).filter(([key, entity]) => !stringIds.includes(key)),
        ),
        entityMetaMap: Object.fromEntries(
          Object.entries(state.entityMetaMap).filter(([key, entity]) => !stringIds.includes(key)),
        ),
      };
    },
    updateOne: (state: EntityState<T>, data: T): EntityState<T> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        [selectId(data)]: {
          ...state.entityMap[selectId(data)],
          ...data,
        },
      },
    }),
    updateMany: (state: EntityState<T>, data: T[]): EntityState<T> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        ...Object.fromEntries(
          data.map(item => [selectId(item), { ...state.entityMap[selectId(item)], ...item }]),
        ),
      },
    }),
    upsertOne: (
      state: EntityState<T>,
      data: T,
      options: EntityAdapterOptions = {},
    ): EntityState<T> => {
      const hasEntity = selectId(data) in state.entityMap;
      return hasEntity ? adapter.updateOne(state, data) : adapter.addOne(state, data, options);
    },
    upsertMany: (
      state: EntityState<T>,
      data: T[],
      options: EntityAdapterOptions = {},
    ): EntityState<T> => {
      const scopeName = resolveScope(options.scope);
      const scopeData = state.scope[scopeName] || [];
      const toUpdate = data.filter(entity => selectId(entity) in state.entityMap);
      const toInsert = data.filter(entity => !(selectId(entity) in state.entityMap));

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          ...Object.fromEntries(
            toUpdate.map(item => [selectId(item), { ...state.entityMap[selectId(item)], ...item }]),
          ),
          ...Object.fromEntries(toInsert.map(item => [selectId(item), item])),
        },
        scope: {
          ...state.scope,
          [scopeName]: [...new Set([...scopeData, ...toInsert.map(selectId)])],
        },
      };
    },
    optimisticUpdateSet: (
      state: EntityState<T>,
      correlationId: string,
      data: OptimisticUpdate<T>,
    ): EntityState<T> => ({
      ...state,
      optimisticUpdate: {
        ...state.optimisticUpdate,
        [correlationId]: data,
      },
    }),
    optimisticUpdateRemove: (state: EntityState<T>, correlationId: string): EntityState<T> => {
      const { [correlationId]: _, ...optimisticUpdate } = state.optimisticUpdate;
      return {
        ...state,
        optimisticUpdate,
      };
    },
    optimisticDeleteSet: (state: EntityState<T>, data: T): EntityState<T> => ({
      ...state,
      optimisticDelete: {
        ...state.optimisticDelete,
        [selectId(data)]: data,
      },
    }),
    optimisticDeleteRemove: (state: EntityState<T>, id: T['id']): EntityState<T> => {
      const { [id]: _, ...optimisticDelete } = state.optimisticDelete;
      return {
        ...state,
        optimisticDelete,
      };
    },
    resetAll: (state: EntityState<T>): EntityState<T> => initialState,
  };

  return adapter;
};
